"use client";

/**
 * Hydration error diagnostic for production.
 *
 * React 18 minifies error messages to just numbers (#418, #423) in
 * production builds, which makes diagnosing hydration mismatches
 * frustratingly opaque. This component captures the full error info
 * via window.error / unhandledrejection listeners and pretty-prints
 * it to the console with the suspect tree state at the moment of
 * the failure.
 *
 * Logs include:
 *   - The minified error code (e.g. #418)
 *   - The decoded URL pointing to the React error decoder for the
 *     full message
 *   - The component stack (when available)
 *   - Suspect state snapshots: tenant store, dateRange-style state,
 *     localStorage keys present, document.documentElement.className,
 *     navigator.language, etc — all the usual SSR↔client divergence
 *     sources, captured exactly when React fails to hydrate.
 *
 * Once the cascade is fixed, remove this component + its providers.tsx
 * mount in the same commit that re-enables strict TS checks.
 */

import { useEffect } from "react";
import { useTenantStore } from "@/store/tenant-store";

const REACT_ERROR_PREFIX = "Minified React error";

function snapshotEnvironment() {
  if (typeof window === "undefined") return {};
  const localStorageKeys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) localStorageKeys.push(key);
    }
  } catch {
    /* access can throw in some privacy modes */
  }
  return {
    href: window.location.href,
    pathname: window.location.pathname,
    htmlClassName: document.documentElement.className,
    bodyClassName: document.body.className,
    navigatorLanguage: navigator.language,
    timezoneOffset: new Date().getTimezoneOffset(),
    localStorageKeys,
    tenantStoreState: useTenantStore.getState(),
    documentReadyState: document.readyState,
    timestamp: new Date().toISOString(),
  };
}

export function HydrationDebugger() {
  useEffect(() => {
    let lastErrorTime = 0;

    const handler = (msg: string, source: string, line: number) => {
      // Only handle React minified errors — avoid spamming on app errors.
      if (!msg.includes(REACT_ERROR_PREFIX)) return;

      // De-dupe rapid bursts (the cascade fires 5x within a frame).
      const now = Date.now();
      if (now - lastErrorTime < 50) return;
      lastErrorTime = now;

      const codeMatch = msg.match(/#(\d{3})/);
      const code = codeMatch ? codeMatch[1] : "?";
      const decoderUrl =
        codeMatch &&
        `https://reactjs.org/docs/error-decoder.html?invariant=${codeMatch[1]}`;

      // eslint-disable-next-line no-console
      console.group(
        `%c[HydrationDebugger] React error #${code}`,
        "color:#f43f5e;font-weight:bold",
      );
      // eslint-disable-next-line no-console
      console.log("Source:", `${source}:${line}`);
      // eslint-disable-next-line no-console
      console.log("Decoded message:", decoderUrl);
      // eslint-disable-next-line no-console
      console.log("Environment snapshot:", snapshotEnvironment());
      // eslint-disable-next-line no-console
      console.groupEnd();
    };

    const errorListener = (event: ErrorEvent) => {
      handler(event.message, event.filename, event.lineno);
    };
    const rejectionListener = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      if (reason instanceof Error) {
        handler(reason.message, "promise", 0);
      } else if (typeof reason === "string") {
        handler(reason, "promise", 0);
      }
    };

    window.addEventListener("error", errorListener);
    window.addEventListener("unhandledrejection", rejectionListener);

    return () => {
      window.removeEventListener("error", errorListener);
      window.removeEventListener("unhandledrejection", rejectionListener);
    };
  }, []);

  return null;
}
